import { GoogleMapsAutocomplete } from '@components/hooks/GooglePlacesAutocompleteProvider/GoogleMapsAutocomplete.client'
import { useFormListener } from '@components/hooks/useFormListener/index.client'
import { Icon } from '@components/primitives/Icon'
import Input from '@components/primitives/Input'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faWarning } from '@fortawesome/pro-regular-svg-icons/faWarning'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { parseAddressComponents } from '@modules/patientAddress/parseAddressComponents'
import {
  RELEASE_DETAILS_ADDRESS_LINE1_INPUT_NAME,
  RELEASE_DETAILS_CITY_INPUT_NAME,
  RELEASE_DETAILS_COUNTRY_INPUT_NAME,
  RELEASE_DETAILS_STATE_INPUT_NAME,
  RELEASE_DETAILS_ZIP_CODE_INPUT_NAME,
  RELEASE_FORM_GOOGLE_AUTOCOMPLETE_INPUT_NAME,
} from '@modules/patientTrialApplications/postApplyFlow/consts'
import type { ApplySuccessPageProps } from 'app/p/apply-success/[token]/[step]/page'
import debounce from 'lodash/debounce'
import { type FormEvent, useEffect, useState } from 'react'
import { type InputNames } from './MedicalReleaseDetailsPrompt'

export const MedicalReleaseAddressInputs = ({
  idToken,
  patientAddress,
}: Pick<ApplySuccessPageProps, 'idToken' | 'patientAddress'>) => {
  const [addressLine1, setAddressLine1] = useState<string>(
    patientAddress?.addressLineOne ?? '',
  )
  const [city, setCity] = useState<string>(patientAddress?.city ?? '')
  const [country, setCountry] = useState<string>(patientAddress?.country ?? '')
  const [state, setState] = useState<string>(patientAddress?.state ?? '')
  const [zipCode, setZipCode] = useState<string>(
    patientAddress?.zipCode?.zip ?? '',
  )
  const [selectedGooglePlace, setSelectedGooglePlace] =
    useState<google.maps.places.PlaceResult>()
  const [showAddressError, setShowAddressError] = useState<boolean>(false)
  const [showFullAddressInputs, setShowFullAddressInputs] =
    useState<boolean>(false)

  const { formRef, formStatus } = useFormListener('apply-success-form')

  const debouncedInputTracking = debounce((type: InputNames, val: string) => {
    trackEvent(TrackingEvents.EMR_RELEASE_INPUT_MODIFIED, {
      trialApplicationIdToken: idToken,
      type,
      val,
    })
  })

  useEffect(() => {
    if (formRef) {
      const handleSubmit = (_event: SubmitEvent) => {
        if (!city || !state || !country || !zipCode || !addressLine1) {
          setShowFullAddressInputs(true)
        }
      }

      formRef.addEventListener('submit', handleSubmit, true)

      return () => {
        formRef.removeEventListener('submit', handleSubmit, true)
      }
    }
  }, [formRef, formStatus, city, state, country, zipCode, addressLine1])

  const setAddressComponentsFromGooglePlace = (
    place: google.maps.places.PlaceResult,
  ) => {
    const addressFields = parseAddressComponents(place)
    setSelectedGooglePlace(place)

    if (!addressFields) {
      return
    }

    debouncedInputTracking('googlePlace', '')

    if (addressFields?.addressLineOne) {
      setAddressLine1(addressFields.addressLineOne)
    }
    if (addressFields?.city) {
      setCity(addressFields.city)
    }
    if (addressFields?.state) {
      setState(addressFields.state)
    }
    if (addressFields?.country) {
      setCountry(addressFields.country)
    }
    if (addressFields?.zip) {
      setZipCode(addressFields.zip)
    }
  }

  return (
    <>
      <div>
        {!showFullAddressInputs && (
          <>
            <Text styleName='p-tiny' value='Your Home Address' />
            <GoogleMapsAutocomplete
              addressClearedCallback={() => setShowAddressError(true)}
              className='h-12 w-full rounded-md border-2 border-neutral200 px-6'
              name={RELEASE_FORM_GOOGLE_AUTOCOMPLETE_INPUT_NAME}
              required={!showFullAddressInputs}
              setPlace={setAddressComponentsFromGooglePlace}
            />
            {showAddressError && !selectedGooglePlace && (
              <Text
                className='m-2 underline'
                onClick={() => {
                  setShowFullAddressInputs(true)
                  trackEvent(
                    TrackingEvents.EMR_RELEASE_ADDRESS_MANUAL_INPUT_CLICKED,
                  )
                }}
                styleName='p-small'
              >
                <Icon className='mr-1 text-warning600' icon={faWarning} />
                Having trouble? Input your address manually
              </Text>
            )}
          </>
        )}
        <div
          className={classMerge('my-4', showFullAddressInputs ? '' : 'hidden')}
        >
          <Text styleName='p-tiny' value='Home Address Line 1' />
          <Input
            autoComplete='address-line1'
            className='h-12 rounded-md bg-white'
            maxLength={128}
            name={RELEASE_DETAILS_ADDRESS_LINE1_INPUT_NAME}
            onInput={(event: FormEvent<HTMLInputElement>) => {
              const value = (event.target as HTMLInputElement).value
              setAddressLine1(value)
              debouncedInputTracking('addressLine1', value)
            }}
            placeholder='789 Main St.'
            required={true}
            value={addressLine1}
          />
        </div>
        <div className='grid grid-cols-2 gap-4 md:grid-cols-3'>
          <div className={showFullAddressInputs ? '' : 'hidden'}>
            <Text styleName='p-tiny' value='City' />
            <Input
              autoComplete='home city'
              className='h-12 rounded-md bg-white'
              maxLength={128}
              name={RELEASE_DETAILS_CITY_INPUT_NAME}
              onInput={(event: FormEvent<HTMLInputElement>) => {
                const value = (event.target as HTMLInputElement).value
                setCity(value)
                debouncedInputTracking('city', value)
              }}
              placeholder='Beverly Hills'
              required={true}
              value={city}
            />
          </div>
          <div className={showFullAddressInputs ? '' : 'hidden'}>
            <Text styleName='p-tiny' value='State' />
            <Input
              autoComplete='region'
              className='h-12 rounded-md bg-white'
              maxLength={128}
              name={RELEASE_DETAILS_STATE_INPUT_NAME}
              onInput={(event: FormEvent<HTMLInputElement>) => {
                const value = (event.target as HTMLInputElement).value
                setState(value)
                debouncedInputTracking('state', value)
              }}
              placeholder='CA'
              required={true}
              value={state}
            />
          </div>
          <div className={showFullAddressInputs ? '' : 'hidden'}>
            <Text styleName='p-tiny' value='Country' />
            <Input
              autoComplete='country'
              className='h-12 rounded-md bg-white'
              maxLength={128}
              name={RELEASE_DETAILS_COUNTRY_INPUT_NAME}
              onInput={(event: FormEvent<HTMLInputElement>) => {
                const value = (event.target as HTMLInputElement).value
                setCountry(value)
                debouncedInputTracking('country', value)
              }}
              placeholder='USA'
              required={true}
              value={country}
            />
          </div>
          <div className={showFullAddressInputs ? '' : 'hidden'}>
            <Text styleName='p-tiny' value='ZIP Code' />
            <Input
              autoComplete='postal-code'
              className='h-12 rounded-md bg-white'
              maxLength={10}
              name={RELEASE_DETAILS_ZIP_CODE_INPUT_NAME}
              onInput={(event: FormEvent<HTMLInputElement>) => {
                const value = (event.target as HTMLInputElement).value
                setZipCode(value)
                debouncedInputTracking('zipCode', value)
              }}
              placeholder='90210'
              required={true}
              value={zipCode}
            />
          </div>
        </div>
      </div>
    </>
  )
}
